import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, useTheme, alpha } from '@mui/material';

interface PageTransitionProps {
  children: React.ReactNode;
}

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  enter: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.98,
    transition: {
      duration: 0.3,
      ease: [0.55, 0.06, 0.68, 0.19],
    },
  },
};

const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const theme = useTheme();

  // Handle ResizeObserver errors at the component level
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message?.includes('ResizeObserver')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  return (
    <motion.div
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageVariants}
      style={{ 
        width: '100%', 
        position: 'relative',
        willChange: 'opacity, transform',
        contain: 'layout style paint',
        minHeight: '0',
        flex: '1 1 auto'
      }}
    >
      <Box 
        width="100%" 
        sx={{ 
          position: 'relative',
          contain: 'layout style paint',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: `radial-gradient(ellipse at top, ${alpha(theme.palette.primary.main, 0.02)}, transparent 50%), 
                       radial-gradient(ellipse at bottom, ${alpha(theme.palette.secondary.main, 0.02)}, transparent 50%)`,
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.01)}, ${alpha(theme.palette.secondary.main, 0.01)})`,
            pointerEvents: 'none',
            zIndex: -1,
          }
        }}
      >
        {children}
      </Box>
    </motion.div>
  );
};

export default PageTransition; 